import { useState } from "react";
import type { SVGProps } from "react";
import {
  useCustom,
  useCustomMutation,
  useGetLocale,
  useList,
  useTranslate,
} from "@refinedev/core";
import type { HttpError } from "@refinedev/core";
import { BuildingIcon, Button, CalendarIcon, Field, Modal, SelectField } from "@fawkes/ui";

import { LoadingState } from "../../components/states";
import { FosterError } from "./FosterError";
import type { Page } from "../../shell/nav";
import type { Locale } from "../../i18n/messages";
import { formatDateLong } from "./format";
import type { AbsenceType, PortalAbsence, PortalContact } from "./types";
import "./beschikbaarheid.css";

// fix/foster-login-redesign — Beschikbaarheid in the foster style (mockup): header + 3 stat cards +
// a "Geplande afwezigheden" table and an "Eerstvolgende afwezigheid" highlight. Same data/contract as
// before — local CRUD against /api/portal/absences (source=foster, server-stamped). The foster can now
// ALSO edit (PUT /api/portal/absences/{id}); 422 (date domain rule), 409 (optimistic-concurrency:
// the row changed since it was listed). Edit/delete are offered only for the foster's OWN (person)
// absences; organization-entered ones are read-only here. Locations come from /api/portal/contact.

const ABSENCE_TYPES: AbsenceType[] = ["vacation", "sick", "other"];

function statusOf(error: unknown): number | undefined {
  return (error as HttpError | undefined)?.statusCode;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function plusDaysIso(iso: string, days: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Inclusive day-count of an ISO date range ("8 juli t/m 29 juli" = 22). */
function daysInclusive(fromIso: string, toIso: string): number {
  const a = Date.parse(`${fromIso}T00:00:00Z`);
  const b = Date.parse(`${toIso}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) {
    return 0;
  }
  return Math.round((b - a) / 86_400_000) + 1;
}

/** Inclusive days of the range that fall inside the given calendar year (for "Totaal afwezig — dit jaar"). */
function daysInYear(fromIso: string, toIso: string, year: number): number {
  const lo = `${year}-01-01`;
  const hi = `${year}-12-31`;
  const start = fromIso < lo ? lo : fromIso;
  const end = toIso > hi ? hi : toIso;
  return end < start ? 0 : daysInclusive(start, end);
}

function PlusIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2.2}
      strokeLinecap="round" aria-hidden="true" {...p}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
function PencilIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}
function TrashIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
    </svg>
  );
}
function InfoIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={17} height={17} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <circle cx={12} cy={12} r={9} /><path d="M12 11v5M12 8h.01" />
    </svg>
  );
}
function CalendarClockIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={22} height={22} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M21 10V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6M16 2v4M8 2v4M3 10h18" />
      <circle cx={17.5} cy={17.5} r={4.5} /><path d="M17.5 15.6v2l1.4 0.9" />
    </svg>
  );
}
function SunIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={22} height={22} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <circle cx={12} cy={12} r={4} />
      <path d="M12 2v2M12 20v2M4 12H2M22 12h-2M5.6 5.6 4.2 4.2M19.8 19.8l-1.4-1.4M18.4 5.6l1.4-1.4M4.2 19.8l1.4-1.4" />
    </svg>
  );
}
function PulseIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={22} height={22} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M3 12h4l2-6 4 12 2-6h6" />
    </svg>
  );
}
function DotsIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={22} height={22} fill="currentColor" aria-hidden="true" {...p}>
      <circle cx={5} cy={12} r={1.7} /><circle cx={12} cy={12} r={1.7} /><circle cx={19} cy={12} r={1.7} />
    </svg>
  );
}

function typeGlyph(type: AbsenceType) {
  if (type === "vacation") {
    return <SunIcon />;
  }
  if (type === "sick") {
    return <PulseIcon />;
  }
  return <DotsIcon />;
}

export function Beschikbaarheid({ onNavigate }: { onNavigate?: (page: Page) => void } = {}) {
  const t = useTranslate();
  const locale = (useGetLocale()() ?? "nl") as Locale;

  const { data, isLoading, isError, refetch } = useList<PortalAbsence>({
    resource: "portal/absences",
    pagination: { mode: "off" },
  });
  // Location names for the "Geldig voor" card — supplementary; never blocks the page if it degrades.
  const contact = useCustom<PortalContact>({ url: "portal/contact", method: "get" });
  const { mutate, isLoading: mutating } = useCustomMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingVersion, setEditingVersion] = useState<number | null>(null);
  const [from, setFrom] = useState(todayIso);
  const [to, setTo] = useState(() => plusDaysIso(todayIso(), 1));
  const [type, setType] = useState<AbsenceType>("vacation");
  const [formError, setFormError] = useState<string | undefined>();
  const [listError, setListError] = useState<string | undefined>();

  function openCreate() {
    setEditingId(null);
    setEditingVersion(null);
    setFormError(undefined);
    setFrom(todayIso());
    setTo(plusDaysIso(todayIso(), 1));
    setType("vacation");
    setModalOpen(true);
  }

  function openEdit(row: PortalAbsence) {
    setEditingId(row.id);
    setEditingVersion(row.version);
    setFormError(undefined);
    setFrom(row.van);
    setTo(row.tot);
    setType(row.type);
    setModalOpen(true);
  }

  // Keep tot ≥ van: when van moves past tot, pull tot along (the picker also disables earlier days via min).
  function changeFrom(value: string) {
    setFrom(value);
    if (to < value) {
      setTo(value);
    }
  }

  function submit() {
    setFormError(undefined);
    if (to < from) {
      setFormError(t("portal.availability.error.range"));
      return;
    }
    const values = { van: from, tot: to, type };
    const request =
      editingId !== null
        ? { url: `portal/absences/${editingId}?version=${editingVersion}`, method: "put" as const, values }
        : { url: "portal/absences", method: "post" as const, values };
    mutate(request, {
      onSuccess: () => {
        setModalOpen(false);
        void refetch();
      },
      onError: (error) => {
        const status = statusOf(error);
        setFormError(
          status === 422
            ? t("portal.availability.error.range")
            : status === 409
              ? t("portal.availability.error.conflict")
              : t("portal.availability.error.generic"),
        );
      },
    });
  }

  function remove(row: PortalAbsence) {
    setListError(undefined);
    mutate(
      { url: `portal/absences/${row.id}?version=${row.version}`, method: "delete", values: {} },
      {
        onSuccess: () => void refetch(),
        onError: (error) => {
          setListError(
            statusOf(error) === 409
              ? t("portal.availability.error.conflict")
              : t("portal.availability.error.deleteFailed"),
          );
        },
      },
    );
  }

  if (isLoading) {
    return <LoadingState />;
  }
  if (isError) {
    return <FosterError onRetry={() => void refetch()} onNavigate={onNavigate} />;
  }

  const rows = data?.data ?? [];
  const today = todayIso();
  const year = new Date().getFullYear();
  const upcoming = [...rows]
    .filter((r) => r.tot >= today)
    .sort((a, b) => a.van.localeCompare(b.van));
  const totalDays = rows.reduce((sum, r) => sum + daysInYear(r.van, r.tot, year), 0);
  const next = upcoming[0] ?? null;
  const locationNames = (contact.data?.data.organizations ?? [])
    .map((o) => o.name)
    .filter((n): n is string => Boolean(n))
    .join(", ");

  function dayLabel(n: number): string {
    return `${n} ${n === 1 ? t("portal.availability.days.one") : t("portal.availability.days.many")}`;
  }
  function isActive(r: PortalAbsence): boolean {
    return r.van <= today && r.tot >= today;
  }
  function rangeLabel(r: PortalAbsence): string {
    return `${formatDateLong(r.van, locale)} ${t("portal.availability.rangeSeparator")} ${formatDateLong(
      r.tot,
      locale,
    )}`;
  }

  return (
    <div className="foster-availability">
      <header className="fa-header">
        <div className="fa-heading">
          <h1>{t("portal.availability.title")}</h1>
          <p>{t("portal.availability.subtitle")}</p>
        </div>
        <button className="fa-newbtn" type="button" onClick={openCreate}>
          <PlusIcon />
          {t("portal.availability.add")}
        </button>
      </header>

      <div className="fa-stats">
        <div className="fa-stat">
          <span className="fa-stat-icon fa-stat-icon--green">
            <CalendarIcon width={22} height={22} />
          </span>
          <div className="fa-stat-body">
            <div className="fa-stat-title">{t("portal.availability.stat.planned.title")}</div>
            <div className="fa-stat-value">{upcoming.length}</div>
            <div className="fa-stat-sub">{t("portal.availability.stat.planned.sub")}</div>
          </div>
        </div>
        <div className="fa-stat fa-stat--amber">
          <span className="fa-stat-icon fa-stat-icon--amber">
            <CalendarClockIcon />
          </span>
          <div className="fa-stat-body">
            <div className="fa-stat-title">{t("portal.availability.stat.total.title")}</div>
            <div className="fa-stat-value">{dayLabel(totalDays)}</div>
            <div className="fa-stat-sub">{t("portal.availability.stat.total.sub")}</div>
          </div>
        </div>
        <div className="fa-stat">
          <span className="fa-stat-icon fa-stat-icon--green">
            <BuildingIcon width={22} height={22} />
          </span>
          <div className="fa-stat-body">
            <div className="fa-stat-title">{t("portal.availability.stat.validFor.title")}</div>
            <div className="fa-stat-value fa-stat-value--sm">{t("portal.availability.allLocations")}</div>
            <div className="fa-stat-sub">{locationNames || "—"}</div>
          </div>
        </div>
      </div>

      <div className="fa-grid">
        <section className="fa-card fa-card--table">
          <div className="fa-card-head">
            <CalendarIcon width={20} height={20} />
            <h2>{t("portal.availability.card.planned")}</h2>
          </div>

          {listError ? (
            <p className="fa-error" role="alert">
              {listError}
            </p>
          ) : null}

          {rows.length === 0 ? (
            <p className="fa-empty">{t("portal.availability.empty")}</p>
          ) : (
            <div className="fa-table-wrap">
              <table className="fa-table">
                <thead>
                  <tr>
                    <th>{t("portal.availability.column.period")}</th>
                    <th>{t("portal.availability.column.type")}</th>
                    <th>{t("portal.availability.column.source")}</th>
                    <th>{t("portal.availability.column.locations")}</th>
                    <th className="fa-th-actions">{t("portal.availability.column.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <div className="fa-period">
                          <span className="fa-period-icon">
                            <CalendarIcon width={18} height={18} />
                          </span>
                          <div>
                            <div className="fa-period-range">{rangeLabel(row)}</div>
                            <div className="fa-period-days">{dayLabel(daysInclusive(row.van, row.tot))}</div>
                          </div>
                        </div>
                      </td>
                      <td>{t(`portal.availability.type.${row.type}`)}</td>
                      <td>
                        <span className={`fa-badge fa-badge--${row.source}`}>
                          {t(`portal.availability.source.${row.source}`)}
                        </span>
                      </td>
                      <td className="fa-muted">{t("portal.availability.allLocations")}</td>
                      <td>
                        {row.source === "person" ? (
                          <div className="fa-actions">
                            <button
                              className="fa-act fa-act--edit"
                              type="button"
                              disabled={mutating}
                              onClick={() => openEdit(row)}
                            >
                              <PencilIcon />
                              {t("portal.availability.edit")}
                            </button>
                            <button
                              className="fa-act fa-act--del"
                              type="button"
                              disabled={mutating}
                              onClick={() => remove(row)}
                            >
                              <TrashIcon />
                              {t("portal.availability.delete")}
                            </button>
                          </div>
                        ) : (
                          <span className="fa-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <aside className="fa-card fa-card--next">
          <div className="fa-card-head">
            <CalendarIcon width={20} height={20} />
            <h2>{t("portal.availability.card.next")}</h2>
          </div>

          {next ? (
            <>
              <div className="fa-next">
                <span className="fa-next-icon">{typeGlyph(next.type)}</span>
                <div className="fa-next-body">
                  <div className="fa-next-top">
                    <span className="fa-next-type">{t(`portal.availability.type.${next.type}`)}</span>
                    <span className="fa-badge fa-badge--planned">
                      {isActive(next)
                        ? t("portal.availability.badge.active")
                        : t("portal.availability.badge.planned")}
                    </span>
                  </div>
                  <div className="fa-next-range">{rangeLabel(next)}</div>
                  <div className="fa-next-days">{dayLabel(daysInclusive(next.van, next.tot))}</div>
                </div>
              </div>
              <p className="fa-next-note">
                <InfoIcon />
                <span>{t("portal.availability.next.note")}</span>
              </p>
            </>
          ) : (
            <p className="fa-empty">{t("portal.availability.next.empty")}</p>
          )}
        </aside>
      </div>

      <Modal
        open={modalOpen}
        title={editingId !== null ? t("portal.availability.dialog.titleEdit") : t("portal.availability.dialog.title")}
        onClose={() => setModalOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              {t("portal.availability.dialog.cancel")}
            </Button>
            <Button onClick={submit} disabled={mutating}>
              {mutating ? t("portal.availability.dialog.saving") : t("portal.availability.dialog.save")}
            </Button>
          </>
        }
      >
        <Field
          label={t("portal.availability.dialog.from")}
          type="date"
          value={from}
          min={editingId !== null ? undefined : today}
          onChange={(e) => changeFrom(e.target.value)}
        />
        <Field
          label={t("portal.availability.dialog.to")}
          type="date"
          value={to}
          min={from}
          onChange={(e) => setTo(e.target.value)}
        />
        <SelectField
          label={t("portal.availability.dialog.type")}
          value={type}
          onChange={(e) => setType(e.target.value as AbsenceType)}
          options={ABSENCE_TYPES.map((value) => ({
            value,
            label: t(`portal.availability.type.${value}`),
          }))}
        />
        {formError ? (
          <p className="fa-error" role="alert">
            {formError}
          </p>
        ) : null}
      </Modal>
    </div>
  );
}
